<template>
	<div class="transaction-export-panel">
		<div class="header">
			<h4>
				{{ t("landAssemblyHeader") }} <span class="count">({{ transactionExportList.size }})</span>
			</h4>
			<button type="button" class="close-btn" :title="t('callToActions.close')" @click="clearExportList">
				<AppIcon :name="IconName.CloseSmall" :size="16" :stroke-width="1.3" />
			</button>
		</div>
		<div class="separator"></div>
		<div class="export">
			<Button
				class="export-btn"
				size="s"
				variant="primary"
				@click="exportTransactions(Array.from(toRaw(transactionExportList).values()))"
			>
				{{ t("export") }}
			</Button>
			<Button
				v-if="canCreateAssemblyTransaction"
				class="assembly-btn"
				size="s"
				variant="secondary"
				:disabled="creatingAssembly"
				@click="emit('create-assembly', Array.from(toRaw(transactionExportList).values()))"
			>
				{{ t("landAssembly.save") }}
			</Button>
		</div>
		<div class="items">
			<TransactionCard
				class="item"
				v-if="isUserLoggedIn && currentUser.has(Permission.TransactionRead) && config.ui.has(Feature.Transactions)"
				v-for="[_, transaction] in transactionExportList"
				:key="transaction.presentationId ?? transaction.inscriptionNumber ?? 0"
				:transaction="transaction"
				:selected="selectedTransaction?.inscriptionNumber == transaction.inscriptionNumber"
				removable
				@mouseenter="handlePreviewedTransaction(transaction)"
				@mouseleave="handlePreviewedTransaction(null)"
				@click="handleSelectedTransaction(transaction)"
				@remove="removeFromExportList(transaction)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, toRaw } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import Papa from "papaparse"
import * as download from "@/utils/download"
import { UseRootStore, Permission } from "@/model/RootStore"
import { config, Feature } from "@/AppConfig"
import type { Transaction } from "@/model/DataModel.ts"
import Button from "@/components/ui/Button.vue"
import TransactionCard from "./TransactionCard.vue"
import { useUnitSystem } from "@/composables/useUnitSystem"
import { createTransactionCsv } from "@/utils/transactionCsv"

defineProps<{
	creatingAssembly?: boolean
}>()

const emit = defineEmits<{
	(e: "create-assembly", transactions: Transaction[]): void
}>()

const { t } = useI18n()
const store = UseRootStore()
const { isUserLoggedIn, selectedTransaction, previewedTransaction, transactionExportList, currentUser } =
	storeToRefs(store)
const { areaUnit } = useUnitSystem()

const canCreateAssemblyTransaction = computed(() => currentUser.value.has(Permission.TransactionWrite))

function handlePreviewedTransaction(transaction: Transaction | null) {
	previewedTransaction.value = transaction ? Number(transaction.presentationId) : null
}

function handleSelectedTransaction(transaction: Transaction) {
	store.updateSelectedTransaction(transaction)
}

function removeFromExportList(transaction: Transaction) {
	const tr = toRaw(transaction)
	if (tr.presentationId == null) return
	store.transactionExportList.delete(tr.presentationId)
}

function clearExportList() {
	store.transactionExportList.clear()
}

function exportTransactions(transactions: Transaction[]) {
	try {
		const { data, columns } = createTransactionCsv(
			transactions,
			areaUnit.value,
			currentUser.value.has(Permission.TransactionSensitiveRead)
		)
		const csv = Papa.unparse(data, { columns })
		const blob = new Blob([csv], { type: "text/csv" })

		download.push(blob, "transactions.csv")
	} catch (error) {
		window.alert(`CSV download failed\n\n${error}`)
	}
}
</script>

<style scoped>
.transaction-export-panel {
	position: fixed;
	top: 0;
	right: 0;
	width: var(--width-transaction-export-panel);
	height: 100%;
	background-color: var(--neutral-0);
	border-left: var(--border-width-thin) solid var(--neutral-100);
	z-index: 10;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);
	padding: var(--spacing-md) var(--spacing-sm);
	font-size: var(--font-size-small);

	.header {
		display: flex;
		justify-content: space-between;
		align-items: center;

		h4 {
			font-size: var(--font-size-base);
			font-family: var(--font-family-primary);
			font-weight: var(--font-weight-medium);
			color: var(--neutral-900);
			margin: 0;

			.count {
				color: var(--neutral-600);
				font-weight: var(--font-weight-regular);
			}
		}

		.close-btn {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: var(--size-icon-xl);
			height: var(--size-icon-xl);
			padding: 0;
			border: none;
			background: none;
			color: var(--neutral-600);
			cursor: pointer;
			transition: color 0.2s ease;

			&:hover {
				color: var(--primary-green);
			}
		}
	}

	.separator {
		width: 100%;
		height: var(--border-width-thin);
		background-color: var(--neutral-400);
	}

	.export {
		display: flex;
		gap: var(--spacing-sm);
		align-items: center;
		font-size: var(--font-size-small);
	}

	.items {
		flex: 1;
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);

		.item {
			margin-right: var(--spacing-xs);
		}
	}
}
</style>
